/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";

/** The 4 available themes: "default" (Neon Cyan dark), "matrix" (Matrix Green dark), "cyberpunk-pink" (Cyberpunk Pink dark), "light" (Light Mode). */
export type ThemeName = "default" | "matrix" | "cyberpunk-pink" | "light";

/** Origin pixel coordinates for the theme transition circle animation. */
interface ThemeOrigin {
  x: number;
  y: number;
}

/** Shape of the theme context exposed to consumers. */
interface ThemeContextState {
  theme: ThemeName;                              /** Current active theme name. */
  setTheme: (t: ThemeName) => void;              /** Directly set a theme (no animation). */
  themeLabel: string;                            /** Human-readable label for the current theme. */
  isDark: boolean;                               /** Whether the current theme is dark. */
  transitionTarget: ThemeName | null;            /** Theme being animated to (null if no transition active). */
  transitionOrigin: ThemeOrigin | null;          /** Click origin for the transition circle animation. */
  startThemeTransition: (t: ThemeName, origin?: ThemeOrigin) => void;  /** Begin animated theme transition. */
  clearTransition: () => void;                   /** End the current animated transition. */
  toggleLightDark: (origin?: ThemeOrigin) => void;  /** Toggle between "light" and last dark theme with optional animation origin. */
}

/** Default context value used as fallback when no ThemeProvider wraps the tree. */
const ThemeCtx = createContext<ThemeContextState>({
  theme: "default",
  setTheme: () => {},                           /** No-op default; overridden by ThemeProvider. */
  themeLabel: "Neon Cyan",
  isDark: true,
  transitionTarget: null,
  transitionOrigin: null,
  startThemeTransition: () => {},               /** No-op default. */
  clearTransition: () => {},                    /** No-op default. */
  toggleLightDark: () => {},                    /** No-op default. */
});

export const useTheme = () => useContext(ThemeCtx);

/** Maps each ThemeName to its human-readable display label. */
export const themeMap: Record<ThemeName, string> = {
  default: "Neon Cyan",
  matrix: "Matrix Green",
  "cyberpunk-pink": "Cyberpunk Pink",
  light: "Light Mode",
};

/** Identifies which themes are considered dark (all except "light"). */
const isDarkTheme: Record<ThemeName, boolean> = {
  default: true,
  matrix: true,
  "cyberpunk-pink": true,
  light: false,
};

/** Background color per theme, used for the theme-color meta tag. */
const metaColors: Record<ThemeName, string> = {
  default: "#040914",
  matrix: "#000000",
  "cyberpunk-pink": "#0a0010",
  light: "#ffffff",
};

/** Color-scheme meta tag value per theme (controls browser UI and default scrollbar styling). */
const schemeValues: Record<ThemeName, string> = {
  default: "dark",
  matrix: "dark",
  "cyberpunk-pink": "dark",
  light: "light",
};

/** Provides theme state, transitions, and persistence to the component tree. */
export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  // Initialize theme from localStorage; fall back to "default" if none saved or if localStorage is unavailable.
  // WHY EXPLICIT WHITELIST: Only accepts "light", "matrix", "cyberpunk-pink" as stored values.
  // "default" is the IMPLICIT fallback when key is missing. This means if localStorage
  // contains an invalid value, it silently falls back to "default".
  const [theme, setThemeState] = useState<ThemeName>(() => {
    try {
      const saved = localStorage.getItem("ma_theme");
      if (saved === "light" || saved === "matrix" || saved === "cyberpunk-pink") return saved;
    } catch {
      // localStorage unavailable
    }
    return "default";
  });

  /**
   * Tracks the last dark theme used so toggleLightDark can restore it when leaving light mode.
   * WHY DEFAULT NOT MATRIX: toggleLightDark ALWAYS toggles to "default", never to "matrix"
   * or "cyberpunk-pink". So if user was on "matrix" and toggles to light, they return to
   * "default", not "matrix". This is intentional: the lock handler in DeveloperMode reverts
   * hacker themes first.
   */
  const lastDarkRef = useRef<ThemeName>("default");

  const [transitionTarget, setTransitionTarget] = useState<ThemeName | null>(null);
  const [transitionOrigin, setTransitionOrigin] = useState<ThemeOrigin | null>(null);

  const isDark = isDarkTheme[theme];

  // Keep lastDarkRef in sync
  useEffect(() => {
    if (isDarkTheme[theme]) lastDarkRef.current = theme;
  }, [theme, isDark]);

  /**
   * Sets the theme directly and persists the choice to localStorage. "default" is stored
   * by removing the key (it is the implicit fallback).
   * WHY REMOVE KEY FOR DEFAULT: Instead of storing "default" in localStorage, the key is
   * removed. This means "default" is the implicit fallback state. Avoids cluttering
   * localStorage with default values.
   */
  const setTheme = useCallback((t: ThemeName) => {
    setThemeState(t);
    try {
      if (t === "default") localStorage.removeItem("ma_theme");
      else localStorage.setItem("ma_theme", t);
    } catch {
      // localStorage unavailable
    }
  }, []);

  /**
   * Begins an animated transition to the given theme, optionally from a specific pixel
   * origin (click position).
   * WHY TWO-PHASE: startThemeTransition triggers overlay animation in ThemeTransition.tsx,
   * which then calls setTheme at the END of the cover phase. This decoupling allows the
   * animation to play before the theme actually changes. If someone calls setTheme directly
   * (not via startThemeTransition), the theme changes instantly without animation.
   */
  const startThemeTransition = useCallback((t: ThemeName, origin?: ThemeOrigin) => {
    setTransitionOrigin(origin ?? null);
    setTransitionTarget(t);
  }, []);

  /** Clears the current transition target and origin, ending any active animated transition. */
  const clearTransition = useCallback(() => {
    setTransitionTarget(null);
    setTransitionOrigin(null);
  }, []);

  /** Toggles between "light" and the last-used dark theme (tracked by lastDarkRef), with an optional click origin for animation. */
  const toggleLightDark = useCallback((origin?: ThemeOrigin) => {
    const next = isDarkTheme[theme] ? "light" : "default";
    setTransitionOrigin(origin ?? null);
    setTransitionTarget(next);
  }, [theme]);

  /**
   * Syncs the DOM with the current theme: sets/removes data-theme attribute on <html>,
   * updates theme-color meta tag, and applies color-scheme for browser-level dark/light UI.
   * WHY COMBINED: These are synchronous DOM writes but combined in one effect to minimize
   * layout thrashing. If theme-color doesn't update in some browsers, check that the meta
   * tag exists in index.html.
   */
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "default") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);

    const metaColor = document.querySelector('meta[name="theme-color"]');
    if (metaColor) metaColor.setAttribute("content", metaColors[theme]);

    const metaScheme = document.querySelector('meta[name="color-scheme"]');
    if (metaScheme) metaScheme.setAttribute("content", schemeValues[theme]);

    root.style.colorScheme = schemeValues[theme];
  }, [theme]);

  return (
    <ThemeCtx.Provider value={{
      theme,
      setTheme,
      themeLabel: themeMap[theme],
      isDark,
      transitionTarget,
      transitionOrigin,
      startThemeTransition,
      clearTransition,
      toggleLightDark,
    }}>
      {children}
    </ThemeCtx.Provider>
  );
};